import type { Edge, Node } from "@xyflow/react";
import type { QueueRunDetail, MonitorStage } from "./types";
import { stageLabel } from "./stageLabel";

export type PipelineNodeData = Record<string, unknown> & {
  label: string;
  state: string;
  stage?: MonitorStage;
  group?: string;
  count?: number;
};

export function buildPipelineGraph(
  detail: QueueRunDetail,
  expanded: ReadonlySet<string>,
): { nodes: Node<PipelineNodeData>[]; edges: Edge[] } {
  const labels = new Map(
    detail.definition.stages.map((stage) => [stage.id, stage.label]),
  );
  const groups = [...detail.definition.groups];
  const stageOrder = new Map(
    detail.definition.stages.map((stage, index) => [stage.id, index]),
  );
  const stagesByGroup = new Map<string, MonitorStage[]>();
  for (const stage of detail.items) {
    const group = stage.stage_id.split(".")[0];
    if (!groups.some((item) => item.id === group))
      groups.push({ id: group, label: "Other observed work" });
    stagesByGroup.set(group, [...(stagesByGroup.get(group) ?? []), stage]);
  }
  const nodes: Node<PipelineNodeData>[] = [];
  const visible = new Map<string, string>();
  groups.forEach((group, column) => {
    const stages = (stagesByGroup.get(group.id) ?? []).sort(
      (left, right) =>
        (stageOrder.get(left.stage_id) ?? Number.MAX_SAFE_INTEGER) -
          (stageOrder.get(right.stage_id) ?? Number.MAX_SAFE_INTEGER) ||
        (left.entity_index ?? Number.MAX_SAFE_INTEGER) -
          (right.entity_index ?? Number.MAX_SAFE_INTEGER) ||
        left.stage_instance_id.localeCompare(right.stage_instance_id),
    );
    if (expanded.has(group.id) && stages.length) {
      stages.forEach((stage, row) => {
        visible.set(stage.stage_instance_id, stage.stage_instance_id);
        nodes.push({
          id: stage.stage_instance_id,
          type: "pipeline",
          position: { x: column * 260, y: row * 135 },
          data: {
            label: stageLabel(stage, labels.get(stage.stage_id)),
            state: stage.state,
            stage,
          },
        });
      });
    } else {
      const id = `group:${group.id}`;
      stages.forEach((stage) => visible.set(stage.stage_instance_id, id));
      const states = new Set(stages.map((stage) => stage.state));
      const state =
        ["running", "retrying", "waiting", "queued", "failed"].find((value) =>
          states.has(value as MonitorStage["state"]),
        ) ??
        (stages.length &&
        stages.every((stage) => ["succeeded", "skipped"].includes(stage.state))
          ? "succeeded"
          : "unknown");
      nodes.push({
        id,
        type: "pipeline",
        position: { x: column * 260, y: 0 },
        data: {
          label: group.label,
          state,
          group: group.id,
          count: stages.length,
        },
      });
    }
  });
  const edges = new Map<string, Edge>();
  const columns = new Map(
    nodes.map((node) => [node.id, node.position.x / 260]),
  );
  for (const stage of detail.items) {
    if (stage.parent_instance_id) {
      const source = visible.get(stage.parent_instance_id);
      const target = visible.get(stage.stage_instance_id);
      // Collapsed groups must not imply a new cross-group dependency.
      if (
        source &&
        target &&
        source !== target &&
        !source.startsWith("group:") &&
        !target.startsWith("group:")
      ) {
        const id = `contains:${source}:${target}`;
        edges.set(id, {
          id,
          source,
          target,
          sourceHandle: "contains",
          type: "pipeline",
          data: { relation: "contains" },
          style: { strokeDasharray: "4 4" },
          ariaLabel: "Contains observed work",
          animated: false,
        });
      }
    }
    for (const dependency of stage.dependency_refs) {
      const source = visible.get(dependency),
        target = visible.get(stage.stage_instance_id);
      if (!source || !target || source === target) continue;
      const id = `${source}:${target}`;
      edges.set(id, {
        id,
        source,
        target,
        type: "pipeline",
        data:
          Math.abs((columns.get(source) ?? 0) - (columns.get(target) ?? 0)) !==
          1
            ? {
                bypassY:
                  -60 -
                  ([...id].reduce(
                    (sum, character) => sum + character.charCodeAt(0),
                    0,
                  ) %
                    6) *
                    24,
              }
            : {},
        animated: false,
      });
    }
  }
  return { nodes, edges: [...edges.values()] };
}
